// ═══════════════════════════════════════════════════════════════════
//  modules/erp/metiers.js
//  METIERS · RARETES_FIOLE · NATURES_RECOMPENSE · helpers d'affichage
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════
//  MODULE MÉTIERS — les huit métiers d'OrialisJobs (ERP joueur)
//  Lecture seule : tables oj_* du plugin OrialisJobs.
//
//  ⚠️ Aucune action staff ici, et ce n'est pas un oubli. Le plugin garde
//  ses joueurs en cache mémoire et ne verse en base que toutes les cinq
//  minutes : une XP donnée depuis l'intranet serait écrasée au versement
//  suivant, sans message et sans trace. Donner de l'XP ou remettre un
//  métier à zéro demandera un pont RCON — c'est un autre chantier.
//
//  ⚠️ LES CLÉS SONT CELLES DE LA BASE, en minuscules sans accent :
//  c'est ce que le plugin écrit dans oj_metier_niveau.metier. Ne pas les
//  « corriger » en pecheur → pêcheur : la jointure se ferait sur du vide.
// ═══════════════════════════════════════════════════════════════════════
export const METIERS = [
  { key: 'bucheron',    label: 'Bûcheron',    emoji: '🪓', color: '#8B6B3D' },
  { key: 'mineur',      label: 'Mineur',      emoji: '⛏️', color: '#7D8590' },
  { key: 'agriculteur', label: 'Agriculteur', emoji: '🌾', color: '#C9A84C' },
  { key: 'chasseur',    label: 'Chasseur',    emoji: '🏹', color: '#F85149' },
  { key: 'pecheur',     label: 'Pêcheur',     emoji: '🎣', color: '#4F8EF7' },
  { key: 'eleveur',     label: 'Éleveur',     emoji: '🐄', color: '#3DDC84' },
  { key: 'explorateur', label: 'Explorateur', emoji: '🧭', color: '#BC8CFF' },
  { key: 'batisseur',   label: 'Bâtisseur',   emoji: '🧱', color: '#E3B341' },
];

export const METIER_PAR_CLE = Object.fromEntries(METIERS.map((m) => [m.key, m]));

// Un métier inconnu de la liste ne doit pas casser l'écran ni disparaître.
export function metierInfo(cle) {
  return METIER_PAR_CLE[cle] || { key: cle, label: cle, emoji: '❓', color: '#7D8590' };
}

// Les quatre raretés de fioles (§ 4.5 du CDC). Le boost est GLOBAL :
// une seule fiole à la fois, pour tous les métiers.
export const RARETES_FIOLE = {
  COMMUNE:    { label: 'Commune',    color: '#7D8590' },
  RARE:       { label: 'Rare',       color: '#4F8EF7' },
  EPIQUE:     { label: 'Épique',     color: '#BC8CFF' },
  LEGENDAIRE: { label: 'Légendaire', color: '#E3B341' },
};

export const NATURES_RECOMPENSE = {
  ARGENT:     { label: 'Argent',     emoji: '💰' },
  OBJET:      { label: 'Objet',      emoji: '📦' },
  PERMISSION: { label: 'Permission', emoji: '🔑' },
  COSMETIQUE: { label: 'Cosmétique', emoji: '✨' },
  ACCES:      { label: 'Accès',      emoji: '🚪' },
  PASSIF:     { label: 'Passif',     emoji: '🌀' },
};

// ── Mise en forme ────────────────────────────────────────────────────
// Espace insécable fine comme séparateur de milliers : c'est la règle
// française, et ça évite qu'un nombre se coupe en fin de ligne.
export function nombre(v, decimales = 0) {
  const n = Number(v || 0);
  return n.toLocaleString('fr-FR', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
}

export function argent(v) { return nombre(v, 2) + ' $'; }

// Les secondes de boost ne s'écoulent qu'en jeu : le minuteur est en pause
// dès que le joueur se déconnecte (décision 6 du schéma). On affiche donc
// une DURÉE, jamais une heure de fin — « il reste 42 min de jeu », pas
// « expire à 19 h 12 », qui serait faux dès la déconnexion.
export function duree(secondes) {
  const s = Math.max(0, Math.round(Number(secondes) || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  if (h > 0) return h + ' h ' + String(m).padStart(2, '0');
  if (m > 0) return m + ' min';
  return s + ' s';
}

export function dateCourte(d) {
  if (!d) return '—';
  const j = new Date(d);
  if (Number.isNaN(j.getTime())) return '—';
  return j.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
